import { lazy, Suspense } from "react";
import { Route, BrowserRouter, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { AppShell } from "@/components/layout/AppShell";
import { LenisProvider } from "@/components/motion/LenisProvider";
import LoginPage from "@/features/auth/LoginPage";
import AgoraPage from "@/features/agora/AgoraPage";
import PinLockScreen from "@/features/auth/PinLockScreen";
import NotFoundPage from "@/features/error/NotFoundPage";

const RegisterPage = lazy(() => import("@/features/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("@/features/auth/AcceptInvitePage"));
const InvitePartnerPage = lazy(() => import("@/features/perfil/InvitePartnerPage"));
const SubscriptionPage = lazy(() => import("@/features/perfil/SubscriptionPage"));
const RaioXPage = lazy(() => import("@/features/raiox/RaioXPage"));
const RaioXResultPage = lazy(() => import("@/features/raiox/RaioXResultPage"));
const DinheiroPage = lazy(() => import("@/features/dinheiro/DinheiroPage"));
const PlanosPage = lazy(() => import("@/features/planos/PlanosPage"));
const GoalDetailPage = lazy(() => import("@/features/planos/GoalDetailPage"));
const VidaPage = lazy(() => import("@/features/vida/VidaPage"));
const PerfilPage = lazy(() => import("@/features/perfil/PerfilPage"));
const RetroPage = lazy(() => import("@/features/retro/RetroPage"));
const AssistentePage = lazy(() => import("@/features/assistente/AssistentePage"));
const JourneyPage = lazy(() => import("@/features/journey/JourneyPage"));

const Splash = () => <div className="min-h-dvh bg-[var(--cream)] flex items-center justify-center"><p className="serif text-2xl text-[var(--coffee-muted)] animate-pulse">Nossa Base</p></div>;

const Protected = ({ children }) => {
  const { user, locked } = useAuth();
  if (locked) return <PinLockScreen />;
  if (user === null) return <Splash />;
  if (!user) return <LoginPage />;
  return <Suspense fallback={<Splash />}>{children}</Suspense>;
};

export default function App() {
  return (
    <LenisProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Splash />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route element={<Protected><AppShell /></Protected>}>
                <Route path="/" element={<AgoraPage />} />
                <Route path="/raio-x" element={<RaioXPage />} />
                <Route path="/raio-x/resultado" element={<RaioXResultPage />} />
                <Route path="/dinheiro" element={<DinheiroPage />} />
                <Route path="/planos" element={<PlanosPage />} />
                <Route path="/planos/:id" element={<GoalDetailPage />} />
                <Route path="/vida" element={<VidaPage />} />
                <Route path="/perfil" element={<PerfilPage />} />
                <Route path="/perfil/convidar" element={<InvitePartnerPage />} />
                <Route path="/perfil/assinatura" element={<SubscriptionPage />} />
                <Route path="/retrospectiva" element={<RetroPage />} />
                <Route path="/assistente" element={<AssistentePage />} />
                <Route path="/jornada" element={<JourneyPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-center" toastOptions={{ style: { background: "#2C221E", color: "#F5F0E5", border: "none", fontFamily: "Manrope" } }} />
      </AuthProvider>
    </LenisProvider>
  );
}
